import { type OutlinedTextFieldProps } from "@mui/material";
import { type ChangeEvent } from "react";

import { TextInput } from "./TextInput";

interface ValidatedTextAreaProps extends Omit<
  OutlinedTextFieldProps,
  "value" | "onChange" | "error" | "helperText" | "multiline" | "variant" | "ref"
> {
  value: string;
  onChange: (value: string) => void;
  /** Minimum required characters (after trimming). Use 0 for an optional field. */
  minCharacters: number;
  /** Maximum allowed characters (after trimming). */
  maxCharacters: number;
  /** Used in error messages, e.g. `${fieldName} must have at least 60 characters`. */
  fieldName: string;
}

interface IsValidTextAreaParams {
  value: string;
  minCharacters: number;
  maxCharacters: number;
}

export function isValidTextArea(params: IsValidTextAreaParams): boolean {
  const { value, minCharacters, maxCharacters } = params;
  const trimmedLength = value.trim().length;
  return trimmedLength >= minCharacters && trimmedLength <= maxCharacters;
}

export function ValidatedTextArea(props: ValidatedTextAreaProps) {
  const {
    value,
    onChange,
    minCharacters,
    maxCharacters,
    fieldName,
    minRows = 2,
    maxRows = 6,
    ...rest
  } = props;

  const trimmedLength = value.trim().length;
  const isTooShort = trimmedLength < minCharacters;
  const isTooLong = trimmedLength > maxCharacters;
  const hasError = (trimmedLength > 0 && isTooShort) || isTooLong;

  let helperText: string;
  if (trimmedLength === 0) {
    helperText =
      minCharacters > 0
        ? `Must have at least ${minCharacters} characters`
        : `${trimmedLength}/${maxCharacters} characters`;
  } else if (isTooShort) {
    helperText = `${fieldName} must have at least ${minCharacters} characters`;
  } else if (isTooLong) {
    helperText = `${fieldName} must have at most ${maxCharacters} characters`;
  } else {
    helperText = `${trimmedLength}/${maxCharacters} characters`;
  }

  return (
    <TextInput
      {...rest}
      multiline
      minRows={minRows}
      maxRows={maxRows}
      value={value}
      error={hasError}
      helperText={helperText}
      onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange(event.target.value);
      }}
    />
  );
}
